import {
  CAMPAIGN_ROOM_ORDER,
  VisualsChoice,
  allKept,
  keepsTooFew,
  keptAtOpening,
  roomsOf,
  sameKept,
  setKept,
  toggleKept,
  visualIdOf,
  visualsChoice,
  visualsInRoom
} from 'utils/socialCampaign/contentKindVisuals'
import { ContentItemDTO, ContentKind, MediaFormat, MediaOrigin, VisualKeyDTO } from 'models/socialCampaign'
import { FC, KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react'
import { GRAY_700, GRAY_800, SELECTED_BLUE, WHITE } from 'constants/styling/theme'
import { PHONE_QUERY, useMatchMedia } from './useMatchMedia'

import { AiFlag } from 'components/common/AiFlag'
import { FilterTab } from 'components/common/FilterTab'
import { MUIButton } from 'components/common/MUIButton'
import { MediaThumb } from 'components/common/MediaThumb'
import Modal from 'components/common/Modals/Modal/Modal'
import { SmartDisplayOutlined } from '@mui/icons-material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import pickerStyles from './PostVisualsPicker.module.sass'
import styles from './ContentKindVisualsDialog.module.sass'
import { useTranslation } from 'react-i18next'

const CheckMark = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
)

const ROOM_KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End']

interface Props {
  kind: ContentKind
  /** The kind's visuals in the viewer's content inventory, in delivery order */
  items: ContentItemDTO[]
  /** The visuals left out as the page shows them (saved, or a choice on its way) */
  excludedVisuals: VisualKeyDTO[]
  /** Whether the campaign draws on the kind, as the page shows it */
  included: boolean
  /** Save with a change: the page saves it and re-plans the drafts */
  onSave: (choice: VisualsChoice) => void
  /** Cancel, Escape, the scrim, the close button, or Save without a change */
  onClose: () => void
}

/**
 * A kind's visuals, kept or left out one by one (ISSUE-3651, prototype v3 `renderBucket`), mounted for each opening:
 * the posts are written only from what is kept. A md dialog, a 92dvh sheet on a phone. Every tile carries its
 * selection mark (an empty circle, a check once kept, no order); what is left out fades. The rooms (ISSUE-3636 labels)
 * are tabs beside the grid on a desktop and scroll as pills over it on a phone, only when the visuals span two rooms or
 * more; "Keep all" / "Leave all out" act on the room shown. No limit; photos keep at least one (Save is disabled and
 * the count says why). Save without a change only closes. The tiles are the editor picker's (`PostVisualsPicker`).
 */
export const ContentKindVisualsDialog: FC<Props> = ({ kind, items, excludedVisuals, included, onSave, onClose }) => {
  const { t } = useTranslation(['social_campaign'])
  const id = `campaign-visuals-${useId().replace(/:/g, '')}`
  const countId = `${id}-count`
  const isPhone = useMatchMedia(PHONE_QUERY)
  // mounted for each opening: it starts from what the page shows
  const [opening, setOpening] = useState(() => keptAtOpening(items, excludedVisuals, included))
  const [kept, setKeptVisuals] = useState(opening)
  const [roomChosen, setRoom] = useState<string | null>(null)
  const seen = useRef(new Set(items.map(visualIdOf)))

  // a visual delivered while open (the inventory read again) is kept, as the backend keeps content added later, unless
  // the kind is left out: never sent back as left out
  useEffect(() => {
    const arrived = items.map(visualIdOf).filter((visual) => !seen.current.has(visual))
    if (!arrived.length) return
    arrived.forEach((visual) => seen.current.add(visual))
    if (!included) return
    setOpening((current) => new Set([...Array.from(current), ...arrived]))
    setKeptVisuals((current) => new Set([...Array.from(current), ...arrived]))
  }, [included, items])

  const rooms = useMemo(() => roomsOf(items), [items])
  // a room a refetch took away (or the tabs gone under two rooms) shows All rooms again
  const room = rooms.length > 1 && rooms.some((entry) => entry.room === roomChosen) ? roomChosen : null
  const shown = room === null ? items : visualsInRoom(items, room)
  const everyShownKept = allKept(kept, shown)
  // what is kept among the visuals still there: one gone while open is neither counted nor checked
  const keptHere = useMemo(() => new Set(items.map(visualIdOf).filter((visual) => kept.has(visual))), [items, kept])
  const blocked = keepsTooFew(kind, keptHere)

  const roomName = (label: string) => CAMPAIGN_ROOM_ORDER.includes(label) ? t(`plan.visuals.room.${label}`) : label
  const tabId = (entry: string | null) => `${id}-room-${entry ?? 'all'}`

  const save = () => {
    if (blocked) return
    if (sameKept(kept, opening)) onClose()
    else onSave(visualsChoice(items, kept))
  }

  const entries = [{ room: null, count: items.length }, ...rooms]
  const onRoomsKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!ROOM_KEYS.includes(event.key)) return
    event.preventDefault()
    const at = entries.findIndex((entry) => entry.room === room)
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1
    const next = event.key === 'Home'
      ? entries[0]
      : event.key === 'End' ? entries[entries.length - 1] : entries[(at + step + entries.length) % entries.length]
    setRoom(next.room)
    document.getElementById(tabId(next.room))?.focus()
  }

  const hasRooms = rooms.length > 1
  const tabs = hasRooms && (
    <div
      className={styles.rooms}
      role="tablist"
      aria-label={t('plan.visuals.rooms')}
      aria-orientation={isPhone ? 'horizontal' : 'vertical'}
      onKeyDown={onRoomsKey}
    >
      {entries.map((entry) => {
        const selected = room === entry.room
        return (
          <FilterTab
            key={entry.room ?? 'all'}
            role="tab"
            id={tabId(entry.room)}
            ariaSelected={selected}
            ariaControls={`${id}-grid`}
            tabIndex={selected ? 0 : -1}
            isActive={selected}
            height="4rem"
            sx={{ flex: 'none', width: { xs: 'auto', sm: '100%' } }}
            label={entry.room === null ? t('plan.visuals.all_rooms') : roomName(entry.room)}
            totalCount={String(entry.count)}
            countLabel={t(`plan.count.${kind}`, { count: entry.count })}
            onClick={() => setRoom(entry.room)}
          />
        )
      })}
    </div>
  )

  const grid = (
    <ul className={pickerStyles.grid}>
      {shown.map((item) => {
        const visual = visualIdOf(item)
        const on = kept.has(visual)
        const name = item.label ?? (item.roomLabel ? roomName(item.roomLabel) : null)
        const fallback = t(`plan.visuals.tile.${kind}`, { index: items.indexOf(item) + 1 })
        const ai = item.origin === MediaOrigin.AI
        const video = item.format === MediaFormat.VIDEO
        const image = item.thumbnailUrl ?? (item.format === MediaFormat.IMAGE ? item.previewUrl : null)
        return (
          <li key={visual}>
            <button
              type="button"
              className={classnames(pickerStyles.tile, styles.tile)}
              aria-pressed={on}
              aria-label={ai ? `${name ?? fallback}, ${t('plan.made_with_ai_all')}` : name ?? fallback}
              onClick={() => setKeptVisuals((current) => toggleKept(current, visual))}
            >

              {/* inline: the kept colour must win over the frame's transparent border */}
              <span className={classnames(pickerStyles.frame, styles.frame)} style={on ? { borderColor: SELECTED_BLUE } : undefined}>

                {video
                  ? <MediaThumb kind="video" src={item.previewUrl ? `${item.previewUrl}#t=0.1` : undefined} still={item.thumbnailUrl ?? undefined} alt="" kindIcon={<SmartDisplayOutlined />} />
                  : item.format === MediaFormat.DOCUMENT
                    ? <MediaThumb kind="pdf" src={image ?? undefined} alt="" kindLabel={t('plan.visuals.pdf')} />
                    : <MediaThumb src={image ?? undefined} alt="" />
                }

                {ai && <AiFlag label={t('plan.visuals.ai_flag')} aria-hidden />}

              </span>

              <span
                className={styles.pick}
                style={on ? { backgroundColor: SELECTED_BLUE, borderColor: SELECTED_BLUE, color: WHITE } : undefined}
                aria-hidden="true"
              >
                <CheckMark />
              </span>

              {name && <Typography variant="text-sm" component="span" color={GRAY_800} className={styles.name} aria-hidden="true">{name}</Typography>}

            </button>
          </li>
        )
      })}
    </ul>
  )

  // on a phone the count is hidden, but why Save is disabled shows, on a row of its own above the actions
  const count = (
    <Typography variant="text-sm" component="span" color={GRAY_700} id={countId} data-blocked={blocked || undefined} hidden={isPhone && !blocked}>
      {blocked ? t('plan.visuals.keep_one_photo') : t('plan.visuals.kept', { kept: keptHere.size, total: items.length })}
    </Typography>
  )
  const toggleAll = shown.length > 0 && (
    <MUIButton type="secondaryNoBorder" size="sm" onClick={() => setKeptVisuals((current) => setKept(current, shown, !everyShownKept))}>
      {everyShownKept ? t('plan.visuals.leave_all_out') : t('plan.visuals.keep_all')}
    </MUIButton>
  )
  const actions = (
    <Stack direction="row" justifyContent="flex-end" gap={1}>

      <MUIButton type="secondaryBorder" onClick={onClose}>{t('plan.visuals.cancel')}</MUIButton>

      <MUIButton disabled={blocked} isFocusableWhenDisabled aria-describedby={countId} onClick={save}>{t('plan.visuals.save')}</MUIButton>

    </Stack>
  )

  return (
    <Modal
      isOpen
      isAccessibleDialog
      size="md"
      isSheetOnPhone
      hasHeaderContentDivider
      title={t(`plan.visuals.title.${kind}`)}
      subtitle={t(`plan.visuals.description.${kind}`)}
      closeLabel={t('plan.visuals.close')}
      onClose={onClose}
      onClickOutside={onClose}
      // the Modal's foot row does not wrap: on a phone the foot is one column, the count on a row of its own
      footerStart={isPhone ? undefined : <>{count}{toggleAll}</>}
      footerContent={isPhone
        ? (
          <Stack gap=".8rem">

            {count}

            <Stack direction="row" alignItems="center" gap={1}>

              <span className={styles.toggleAll}>{toggleAll}</span>

              {actions}

            </Stack>

          </Stack>
        )
        : actions
      }
    >
      <div className={hasRooms ? styles.withRooms : undefined}>

        {tabs}

        {hasRooms
          ? <div role="tabpanel" id={`${id}-grid`} aria-labelledby={tabId(room)}>{grid}</div>
          : grid
        }

      </div>
    </Modal>
  )
}
